/**
 * T-044: typed API client for the unit_qr_codes lifecycle endpoints
 * (T-040 / T-042 / T-043 / T-044). All routes return JSON; errors are
 * thrown with the raw response body attached on .body so callers can
 * surface error.code / error.conflict / error.preview to the UI.
 */

import { authenticatedFetch } from '@/lib/api';
import type {
  QrCodeListResponse,
  QrCodeMutationResponse,
  QrCodeUploadErrorBody,
  UnitQrCodeListResponse,
} from '@/types/qr';

class QrApiError extends Error {
  status: number;
  body: QrCodeUploadErrorBody;
  constructor(status: number, body: QrCodeUploadErrorBody) {
    super(body?.message || body?.code || `Request failed with status ${status}`);
    this.name = 'QrApiError';
    this.status = status;
    this.body = body;
  }
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  const ct = res.headers.get('content-type') || '';
  const body = ct.includes('application/json') ? await res.json() : null;
  if (!res.ok) {
    throw new QrApiError(res.status, body || { error: 'Error', code: 'unknown' });
  }
  return body as T;
}

/**
 * GET /admin/unit-qr-codes — cross-unit listing of every per-unit QR
 * the caller can see. Powers the "Per-Unit QR Codes" section on
 * /settings/qr-codes so styled and per-unit QRs share one page.
 */
export async function listAllUnitQrCodes(opts: {
  includeInactive?: boolean;
} = {}): Promise<UnitQrCodeListResponse> {
  const params = new URLSearchParams();
  if (opts.includeInactive === false) {
    params.set('include_inactive', 'false');
  }
  const qs = params.toString();
  const res = await authenticatedFetch(
    `/api/v1/admin/unit-qr-codes${qs ? `?${qs}` : ''}`,
  );
  return jsonOrThrow<UnitQrCodeListResponse>(res);
}

/**
 * GET /admin/units/:unitId/qr-codes — full history (T-044).
 */
export async function listUnitQrCodes(unitId: string): Promise<QrCodeListResponse> {
  const res = await authenticatedFetch(
    `/api/v1/admin/units/${encodeURIComponent(unitId)}/qr-codes`,
  );
  return jsonOrThrow<QrCodeListResponse>(res);
}

/**
 * POST /admin/units/:unitId/qr-codes/regenerate — mint a fresh generated_v1
 * row + atomic activate (T-044). T-109: superadmin-only, gated by the exact
 * typed phrase `Regenerate <unit_code> QR Code`.
 */
export async function regenerateUnitQr(
  unitId: string,
  confirmPhrase: string,
): Promise<QrCodeMutationResponse> {
  const res = await authenticatedFetch(
    `/api/v1/admin/units/${encodeURIComponent(unitId)}/qr-codes/regenerate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirm_phrase: confirmPhrase }),
    },
  );
  return jsonOrThrow<QrCodeMutationResponse>(res);
}

/** The exact phrase a superadmin must type to regenerate a unit's QR. */
export function regeneratePhraseFor(unitCode: string): string {
  return `Regenerate ${unitCode} QR Code`;
}

/**
 * POST /admin/units/:unitId/qr-codes (multipart) — upload a legacy
 * sticker (T-042).
 */
export async function uploadUnitQr(
  unitId: string,
  file: File,
  opts: { label?: string; allowArbitrary?: boolean } = {},
): Promise<QrCodeMutationResponse> {
  const fd = new FormData();
  fd.set('mode', 'upload');
  fd.set('image', file);
  if (opts.label) fd.set('label', opts.label);
  if (opts.allowArbitrary) fd.set('allow_arbitrary_payload', 'true');

  const res = await authenticatedFetch(
    `/api/v1/admin/units/${encodeURIComponent(unitId)}/qr-codes`,
    { method: 'POST', body: fd },
  );
  return jsonOrThrow<QrCodeMutationResponse>(res);
}

/** The T-109 permanence ceremony: type the exact unit code + a reason. */
export interface QrCeremony {
  confirmUnitCode: string;
  reason: string;
}

function ceremonyBody(c: QrCeremony) {
  return {
    method: 'POST' as const,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirm_unit_code: c.confirmUnitCode, reason: c.reason }),
  };
}

/**
 * POST /admin/units/:unitId/qr-codes/:qrId/activate — restore.
 *
 * Re-activating an ARCHIVED code reverses the archive and requires the ceremony
 * (superadmin + typed code + reason); restoring a merely-retired code does not.
 */
export async function restoreQrCode(
  unitId: string,
  qrId: string,
  ceremony?: QrCeremony,
): Promise<QrCodeMutationResponse> {
  const url = `/api/v1/admin/units/${encodeURIComponent(unitId)}/qr-codes/${encodeURIComponent(qrId)}/activate`;
  const res = await authenticatedFetch(url, ceremony ? ceremonyBody(ceremony) : { method: 'POST' });
  return jsonOrThrow<QrCodeMutationResponse>(res);
}

/**
 * POST /admin/units/:unitId/qr-codes/:qrId/archive — soft-retire (T-109).
 * Superadmin ceremony: type the exact unit code + a reason. Audited into the
 * unit's history. The code is NEVER destroyed and still resolves when scanned.
 */
export async function archiveQrCode(
  unitId: string,
  qrId: string,
  ceremony: QrCeremony,
): Promise<QrCodeMutationResponse> {
  const res = await authenticatedFetch(
    `/api/v1/admin/units/${encodeURIComponent(unitId)}/qr-codes/${encodeURIComponent(qrId)}/archive`,
    ceremonyBody(ceremony),
  );
  return jsonOrThrow<QrCodeMutationResponse>(res);
}

interface ActiveQrImageResponse {
  signed_url: string;
  expires_at: string;
}

/**
 * Fetch a fresh 5-min signed Storage URL for the unit's active QR.
 *
 * The endpoint is auth-gated (requireAdmin), so the call MUST go through
 * authenticatedFetch — `<img src=...>` can't attach a Bearer header on
 * its own, which is why this is async + a separate fetch instead of a
 * synchronous URL that an <img> could consume directly.
 */
export async function fetchActiveQrImageUrl(unitId: string): Promise<string> {
  const res = await authenticatedFetch(
    `/api/v1/admin/units/${encodeURIComponent(unitId)}/qr-codes/active/image`,
  );
  const body = await jsonOrThrow<ActiveQrImageResponse>(res);
  return body.signed_url;
}

/**
 * Fetch a signed DOWNLOAD URL for the unit's active QR (PNG, SVG, or a
 * print-ready PDF). The image is pre-generated with the unit code printed under
 * it; the signed URL forces a download with a `<unit_code>-QR.<ext>` filename.
 * For `pdf`, `layout` picks the 8.5x11 sheet ('letter') or the tight square
 * ('card'). Open the returned URL to start the download.
 */
export async function fetchQrDownloadUrl(
  unitId: string,
  format: 'png' | 'svg' | 'pdf',
  layout?: 'letter' | 'card',
): Promise<string> {
  const layoutQuery = format === 'pdf' && layout ? `&layout=${layout}` : '';
  const res = await authenticatedFetch(
    `/api/v1/admin/units/${encodeURIComponent(unitId)}/qr-codes/active/download?format=${format}${layoutQuery}`,
  );
  const body = await jsonOrThrow<ActiveQrImageResponse & { format?: string }>(res);
  return body.signed_url;
}

export { QrApiError };
